'use client';

import { useState, type Ref } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CONFIRM_DWELL_MS } from './coveragePresentation';
import type { RotationControl } from './coverageTypes';

/**
 * THE ONE THING THE ROTATION NEEDS FROM A HUMAN RIGHT NOW.
 *
 * Eight buttons used to sit in a wrap under the forecast — Approve, Rotate now, Cancel, Suspend,
 * Resume, Detach, Force detach — most of them permanently disabled, most of the time. A row of
 * controls that is usually inert trains an operator to skip it, which is the worst possible
 * property for the row that also carries "approve this launch".
 *
 * So this card exists only when there is a decision to make about a SWAP: a standing proposal to
 * approve, bring forward or cancel, or a forecast that could not be read at all. Suspend, Resume
 * and Detach are not here — they are deliberate interventions in the capability's life, not
 * responses to this cycle, and they do not belong in the same row as Approve.
 *
 * THAT GATE IS PART OF THIS COMPONENT'S CONTRACT, and it enforces its own rather than trusting a
 * caller two hundred lines away: the refusal sentences below are written for the states it is
 * rendered in, and in the state it is not, the first of them would make a false claim about the
 * fleet.
 *
 * TWO THINGS SURVIVED THE MOVE, and they are the acceptance criteria for it:
 *
 *   1. THE PRE-PRESS REFUSALS. Every disabled control says WHY, in words, on the screen — never
 *      in a `title`. `disabled:pointer-events-none` on the Button base means a native tooltip on a
 *      disabled control never fires hover, and a disabled button takes no focus, so a tooltip
 *      reason is unreachable by mouse, keyboard and screen reader alike. This component has a
 *      standing rule against them, recorded as a previous finding against it.
 *   2. THE 600 ms ARM-THEN-CONFIRM DWELL on `rotate_now` — the one control here whose press leads
 *      to an airframe leaving a pad.
 *
 * `aria-disabled` with a guarded handler throughout, NOT `disabled`: a focused button that becomes
 * `disabled` mid-press drops focus to `<body>` in Chromium, and `disabled` also silences
 * `aria-busy`.
 */
export function CoverageActionCard({
  proposalVisible, hasProposal, rotationFailed, launchUnderWay, selfSwapRotation, automaticLaunch,
  canApprove, canRotateNow, busy, submitting, rotateArmed, armedRotateAt, approved, approvedNote,
  subject, children, onApprove, onArmRotate, onRotateNow, onDisarm, onCancel, uid, focusRef,
  compact = false,
}: {
  /** Is a forecast DRAWN on the panel — from the socket frame or the poll, whichever is fresher? */
  proposalVisible: boolean;
  /**
   * Has the poll read back the ROW? Only then is there an action id to command against.
   *
   * Not the same question as `proposalVisible`: a socket frame can put the forecast on the board
   * up to a poll interval before the row exists, and in that window the operator can see the
   * decision but the platform cannot yet act on it.
   */
  hasProposal: boolean;
  rotationFailed: boolean;
  launchUnderWay: boolean;
  selfSwapRotation: boolean;
  automaticLaunch: boolean;
  canApprove: boolean;
  canRotateNow: boolean;
  busy: boolean;
  /** The panel's own union, not `string`: a renamed control must not silently lose its busy label. */
  submitting: null | 'attach' | 'detach' | 'force' | RotationControl;
  rotateArmed: boolean;
  armedRotateAt: number | null;
  onApprove: () => void;
  onArmRotate: () => void;
  onRotateNow: () => void;
  onDisarm: () => void;
  onCancel: () => void;
  /** Has an operator already armed this exact proposal? Then nothing here is outstanding. */
  approved: boolean;
  /** Who approved it and when, in the operator's words. Null unless `approved`. */
  approvedNote: string | null;
  /** The one line that says which swap this is — relief and launch time. */
  subject: string | null;
  /** The standing "what approving actually does" copy, which belongs inside the box it explains. */
  children?: React.ReactNode;
  uid: string;
  /**
   * The card's own container, so the panel can bring it into view and put focus on it when a
   * timeline bar sends the operator here (PR 4).
   *
   * ON THE CONTAINER, NOT ON APPROVE. Focusing the button directly would land the operator on a
   * control that is `aria-disabled` for most of a healthy rotation's life, and would skip the
   * card's heading and the sentence under it that says what approving actually does in this
   * deployment. The container carries `aria-labelledby`, so focusing it announces the card.
   */
  focusRef?: Ref<HTMLDivElement>;
  /**
   * Draw for a narrow column - the COP's 288-320 px mission strip rather than the mission page.
   *
   * WHAT IT CHANGES IS WHEN A SENTENCE IS SEEN, NEVER WHETHER IT EXISTS. Every refusal renders
   * exactly as it does on the wide surface, unconditionally, because a refusal an operator has to
   * go looking for is one they will act without. What collapses is the pair of STANDING notes for
   * controls that are AVAILABLE - and they collapse to `sr-only`, not out of the DOM, so both
   * `aria-describedby` targets still resolve and a screen reader still reads the consequence of
   * every press. The sighted operator gets them from the expander, or automatically the moment
   * ROTATE NOW is armed, which is before the press that matters and after the one that does not.
   *
   * On the COP these two notes were 52 of the 79 words in the resting state, in a column where the
   * whole block has to be scannable in the three seconds the operational standard allows.
   */
  compact?: boolean;
}) {
  /**
   * Whether `compact` is currently showing its two standing notes. Declared above the early return
   * below, because a hook after a conditional `return null` is a hook that is not always called.
   */
  const [notesOpen, setNotesOpen] = useState(false);

  // ITS OWN CONTRACT, ENFORCED HERE. The refusal sentences below are written for the states this
  // card is rendered in; with nothing visible and nothing failed, the first of them would assert
  // "no aircraft in this rotation can relieve the station", which is a claim about the fleet and
  // would be false. A gate two hundred lines away in the caller is not what should be keeping a
  // safety sentence honest.
  if (!proposalVisible && !rotationFailed) return null;

  /**
   * ALREADY APPROVED IS A REFUSAL, not a live control.
   *
   * `canApprove` asks whether the proposal is arm-ABLE; it does not know whether somebody already
   * armed it. Left pressable, the card kept asking for a decision that had been made — and the
   * only place saying otherwise was the forecast block above, outside the box.
   */
  const approveAvailable = canApprove && !approved;

  /**
   * ARMING ROTATE NOW OPENS THE NOTES, whether or not the operator asked for them.
   *
   * This is the press that ends with an airframe leaving a pad, and the standing version of the
   * sentence existed so nobody could reach that press without having read it. Showing it between
   * the arm and the confirm keeps that guarantee on a narrow column: it is the same grammar Detach
   * already uses two components away, so the surface has one answer to "press, read, confirm"
   * rather than two.
   */
  const notesShown = !compact || notesOpen || rotateArmed;
  const noteClass = (base: string) => (notesShown ? base : `${base} sr-only`);

  return (
    <div
      ref={focusRef}
      // -1, so it is reachable programmatically and never lands in the tab order: the card is a
      // group of controls, and adding a stop before them would make every keyboard pass through
      // this panel one press longer.
      tabIndex={-1}
      data-testid="coverage-action-card"
      role="group"
      aria-labelledby={`${uid}-decision-heading`}
      className={cn(
        'space-y-2 rounded border p-3',
        // `focus:`, NOT `focus-visible:`. The only way into this container is programmatic, from
        // the timeline's decision bar, and a pointer-initiated programmatic focus does not match
        // `:focus-visible` — the operator would be moved somewhere with nothing drawn, and their
        // next Tab would start from a place they were never shown. There is no global focus ring
        // to fall back on: `globals.css` sets a ring COLOUR on `*` and no width.
        'focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-foreground',
        // AMBER ONLY WHILE SOMETHING IS OUTSTANDING. `current` stands for most of a healthy
        // rotation's life, so a permanently amber box would trade "a row that is usually
        // disabled" for "a warning that is usually there" — on a panel where amber already means
        // NO RELIEF, SUSPENDED, STALE and a failed read. Once the decision is made the box quiets
        // to the panel's ordinary border and keeps only the record of it.
        //
        // The established callout weights, not a new pair: `--status-amber-marker` is defined as
        // the weight that holds 3:1 for a non-text graphic, and putting it at 60% alpha throws
        // away the one property it exists for.
        approved ? 'border-border' : 'border-status-amber/40 bg-status-amber/10',
      )}
    >
      {/* WHAT THE DECISION IS ABOUT, on the card. Who relieves, when it launches and the commit
          deadline are in the forecast block above — which on a phone is four stacked read rows
          plus an advisory inside a 45vh scroller, so approving meant scrolling away from the time
          being approved. It also gives the group an accessible name: without one, a screen reader
          reads three buttons in a flat sequence with nothing saying they are one decision. */}
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span
          id={`${uid}-decision-heading`}
          className="text-[11px] uppercase tracking-wider text-muted-foreground"
        >
          {approved ? 'Replacement · approved' : 'Replacement · needs a decision'}
        </span>
        {subject !== null && (
          <span className="text-[11px] font-mono tabular-nums text-foreground">{subject}</span>
        )}
      </div>

      {approvedNote !== null && (
        <p className="text-xs text-muted-foreground">{approvedNote}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/* Enabled ONLY on a live proposal that names an aircraft. Approving a rotation with no
            relief arms nothing — §13 keeps publishing the time so the outgoing aircraft still
            knows when to leave, and the server refuses with exactly that sentence. */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          aria-disabled={busy || !approveAvailable}
          aria-busy={submitting === 'approve'}
          aria-describedby={approveAvailable ? `${uid}-controls-note` : `${uid}-approve-why`}
          onClick={() => { if (!busy && approveAvailable) onApprove(); }}
        >
          {/* WHAT IS BEING APPROVED, and on a rotation of one it is not a launch (#564). The
              orchestrator's own words: "what an operator is approving is not a launch, it is a
              RECALL - the decision to take the only aircraft off the station and accept the gap."
              A button that says "Approve replacement" for that is the wrong sentence at 02:00. */}
          {submitting === 'approve'
            ? 'Approving…'
            : selfSwapRotation ? 'Approve recall' : 'Approve replacement'}
        </Button>

        {/* ROTATE NOW (§14's fifth control, W4). Beside Approve because it is the same decision
            made about a different time — and it is the one control here whose press leads to an
            aircraft leaving a pad, so it says so on the line below rather than in a tooltip. */}
        <Button
          size="sm"
          variant={rotateArmed ? 'destructive' : 'outline'}
          className="h-8 text-xs"
          aria-disabled={busy || !canRotateNow}
          aria-busy={submitting === 'rotate_now'}
          aria-describedby={canRotateNow ? `${uid}-rotate-note` : `${uid}-rotate-why`}
          onClick={() => {
            if (busy || !canRotateNow) return;
            if (!rotateArmed) { onArmRotate(); return; }
            // The same dwell Detach uses — it defeats a double-tap and a stray second click
            // landing on a button that has just changed meaning.
            if (Date.now() - (armedRotateAt ?? 0) < CONFIRM_DWELL_MS) return;
            onRotateNow();
          }}
        >
          {submitting === 'rotate_now'
            ? 'Bringing forward…'
            : rotateArmed ? 'Confirm rotate now' : 'Rotate now'}
        </Button>
        {rotateArmed && (
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onDisarm}>
            Keep the schedule
          </Button>
        )}

        <Button
          size="sm"
          variant="ghost"
          className="h-8 text-xs"
          aria-disabled={busy || !hasProposal}
          aria-busy={submitting === 'abort_replacement'}
          // Pointed at whichever line is actually rendered. It used to name the consequence note
          // unconditionally, which dangles in the state where there is no proposal — a button with
          // a broken description and no reason at all for a screen-reader operator.
          aria-describedby={hasProposal ? `${uid}-cancel-note` : `${uid}-cancel-why`}
          onClick={() => { if (!busy && hasProposal) onCancel(); }}
        >
          {submitting === 'abort_replacement' ? 'Cancelling…' : 'Cancel replacement'}
        </Button>
      </div>

      {/* WHY APPROVE IS UNAVAILABLE, ON SCREEN AND NOT IN A TOOLTIP.
          BRANCHED, because "we could not read the forecast" is not "nobody can relieve the
          station" — the same distinction the panel keeps everywhere else, and asserting the second
          when the first is true is a lie about the fleet. */}
      {!approveAvailable && (
        <p id={`${uid}-approve-why`} className="text-xs text-status-amber-fg">
          {rotationFailed
            ? 'Approve is unavailable — the replacement schedule could not be read.'
            : approved
              ? 'Already approved — there is nothing further to arm on this replacement.'
              : !hasProposal
                ? 'Approve is unavailable — this replacement has only just been published and the'
                  + ' panel has not read it back yet. It becomes available within seconds.'
                : 'Approve is unavailable — no aircraft in this rotation can relieve'
                  + ' the station, so there is nothing to arm.'}
        </p>
      )}

      {/* Same rule as Approve's reason above: on the screen, never in a tooltip. The extra branch
          is the one Approve does not have — a launch already under way cannot be brought forward,
          and that is a different sentence from "nothing is scheduled". */}
      {!canRotateNow && (
        <p id={`${uid}-rotate-why`} className="text-xs text-status-amber-fg">
          {launchUnderWay
            ? 'Rotate now is unavailable — this replacement is already under way.'
            : rotationFailed
              ? 'Rotate now is unavailable — the replacement schedule could not be read.'
              : !hasProposal
                ? 'Rotate now is unavailable — this replacement has only just been published and'
                  + ' the panel has not read it back yet. It becomes available within seconds.'
                : 'Rotate now is unavailable — no aircraft in this rotation can'
                  + ' relieve the station, so there is nothing to launch.'}
        </p>
      )}

      {/*
        THE WAY BACK TO THE TWO NOTES ON A NARROW COLUMN.

        AN INLINE EXPANDER, NOT A TOOLTIP. This component's standing rule is that an explanation
        reachable only by hover is unreachable on a field tablet, by keyboard, and by a screen
        reader - it is why the refusals above are on screen and why the Timeline button next door is
        enabled with its reason written out rather than disabled with its reason in a `title`. A
        press works everywhere a press works.

        ABSENT WHILE ROTATE NOW IS ARMED, because the notes are open by force at that moment and a
        control that appears to close them but cannot is worse than no control. It is also absent
        when neither note is rendered - there is nothing behind it to reveal.

        IT SITS ABOVE WHAT IT REVEALS. It shipped below them, so opening it inserted four lines of
        text ABOVE the button that had just been pressed: in a 288 px column the control jumped
        50-60 px out from under the operator's finger and the revealed text landed where they were
        not looking. A second tap - the natural reaction to "nothing happened where I pressed" -
        closed it straight back again.
      */}
      {compact && !rotateArmed && (canRotateNow || hasProposal) && (
        <button
          type="button"
          onClick={() => setNotesOpen((wasOpen) => !wasOpen)}
          aria-expanded={notesOpen}
          className={cn(
            'text-[10px] uppercase tracking-wider underline underline-offset-2 cursor-pointer',
            'text-muted-foreground hover:text-foreground',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-sm',
            // ~13 px of text is a thin target on a field tablet. The negative margin buys the
            // height back without moving anything around it.
            'py-1 px-1 -mx-1 -my-1',
          )}
        >
          {/* "These" was unanchored, and it said "these" even when only one note existed. */}
          {notesOpen ? 'Hide what these buttons do' : 'What these buttons do'}
        </button>
      )}

      {/* WHAT PRESSING IT ACTUALLY DOES, standing rather than conditional — the same discipline
          D19-W3 applied to Approve's copy after the review found the surface over-promising.
          Approve ARMS; this one makes the launch DUE, and the orchestrator is what commands it. On
          a deployment where the orchestrator is switched off, nothing flies and the operator must
          not be left to infer that from silence. */}
      {canRotateNow && (
        <p id={`${uid}-rotate-note`} className={noteClass('text-xs text-status-amber-fg')}>
          {selfSwapRotation
            ? (automaticLaunch
              ? 'This rotation has ONE aircraft. Rotate now recalls it immediately'
                + ' and approves that: the station goes uncovered within seconds and'
                + ' stays uncovered until it is resupplied and back up.'
              : 'This rotation has ONE aircraft. Rotate now makes its recall due and'
                + ' approves it. Automatic launching is OFF here, so once it is down'
                + ' nothing will send it back up without someone launching it.')
            : (automaticLaunch
              ? 'Rotate now brings the launch forward to this moment and approves it.'
                + ' The rotation will command the aircraft within seconds.'
              : 'Rotate now brings the launch forward to this moment and approves it.'
                + ' Automatic launching is OFF here, so the replacement becomes due'
                + ' and waits for someone to launch it from its dock.')}
        </p>
      )}

      {/* THE CONSEQUENCE, SAID IN WORDS, and its own full-width line rather than a trailing span.
          On any viewport where the buttons share a row it rendered to the right of the last button
          and read as that button's caption. */}
      {hasProposal ? (
        <p id={`${uid}-cancel-note`} className={noteClass('text-xs text-muted-foreground')}>
          Cancelling disarms this replacement. A new, unapproved one is scheduled
          straight away — the station still has to be relieved.
        </p>
      ) : (
        // NEVER COLLAPSED, on any width. This is a refusal.
        <p id={`${uid}-cancel-why`} className="text-xs text-status-amber-fg">
          {rotationFailed
            ? 'Cancel is unavailable — the replacement schedule could not be read.'
            : 'Cancel is unavailable — this replacement has only just been published and the panel'
              + ' has not read it back yet.'}
        </p>
      )}

      {/* THE STANDING CONSEQUENCE OF APPROVING, inside the box it explains. Left in the panel it
          rendered after Suspend and Suspend's own note, so it read as that button's caption — the
          exact mistake this file's cancel note carries a comment about. */}
      {children}
    </div>
  );
}
